import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Button, IconButton, MenuItem, Popover, Select, Stack } from '@mui/material';
import { MenuIcon } from '@sharedCR/coreDesignSystem/icons';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';

import {
  anchorOrigin,
  filterIconButtonStyles,
  filterInputStyles,
  filterModeSelectStyles,
  popoverContentStyles,
  popoverSlotProps,
  transformOrigin,
} from './ColumnFilterMenu.styles';
import { ColumnFilterMenuTestIds } from './ColumnFilterMenu.testIds';

const FILTER_MODES = {
  CONTAINS: 'contains',
  EQUALS: 'equals',
  STARTS_WITH: 'startsWith',
  ENDS_WITH: 'endsWith',
};

const FILTER_MODE_LABELS = {
  [FILTER_MODES.CONTAINS]: 'Contains',
  [FILTER_MODES.EQUALS]: 'Equals',
  [FILTER_MODES.STARTS_WITH]: 'Starts with',
  [FILTER_MODES.ENDS_WITH]: 'Ends with',
};

const ColumnFilterMenu = ({
  columnKey,
  filterValue = '',
  filterMode = FILTER_MODES.CONTAINS,
  onFilterChange,
  externalAnchorEl,
  onClose: onExternalClose,
}) => {
  const { anchorEl: internalAnchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isControlled = !!externalAnchorEl;
  const anchorEl = isControlled ? externalAnchorEl : internalAnchorEl;

  const [localValue, setLocalValue] = useState(filterValue);
  const [localMode, setLocalMode] = useState(filterMode);

  const isOpen = !!anchorEl;
  const isFilterActive = !!filterValue;

  const iconButtonClassName = useMemo(
    () => `column-filter-icon${isFilterActive ? ' filter-active' : ''}`,
    [isFilterActive],
  );

  const handleOpen = useCallback(
    e => {
      e.stopPropagation();
      setLocalValue(filterValue);
      setLocalMode(filterMode);
      onSetAnchorElement(e);
    },
    [filterValue, filterMode, onSetAnchorElement],
  );

  const closePopover = useCallback(() => {
    if (isControlled) {
      onExternalClose();

      return;
    }

    onClearAnchorElement();
  }, [isControlled, onExternalClose, onClearAnchorElement]);

  const handleClose = useCallback(() => {
    closePopover();
  }, [closePopover]);

  const handleModeChange = useCallback(e => {
    setLocalMode(e.target.value);
  }, []);

  const handleValueChange = useCallback(e => {
    setLocalValue(e.target.value);
  }, []);

  const handleApply = useCallback(() => {
    onFilterChange(columnKey, { value: localValue, mode: localMode });
    closePopover();
  }, [columnKey, localValue, localMode, onFilterChange, closePopover]);

  const handleClear = useCallback(() => {
    setLocalValue('');
    setLocalMode(FILTER_MODES.CONTAINS);
    onFilterChange(columnKey, { value: '', mode: FILTER_MODES.CONTAINS });
    closePopover();
  }, [columnKey, onFilterChange, closePopover]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        handleApply();
      }
    },
    [handleApply],
  );

  return (
    <>
      {!isControlled && (
        <IconButton
          disableRipple
          className={iconButtonClassName}
          data-testid={ColumnFilterMenuTestIds.TRIGGER}
          size="small"
          sx={filterIconButtonStyles}
          onClick={handleOpen}
        >
          <MenuIcon size={IconSize[16]} />
        </IconButton>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        open={isOpen}
        slotProps={popoverSlotProps}
        transformOrigin={transformOrigin}
        onClose={handleClose}
      >
        <Box sx={popoverContentStyles}>
          <Select
            data-testid={ColumnFilterMenuTestIds.MODE_SELECT}
            size="small"
            sx={filterModeSelectStyles}
            value={localMode}
            onChange={handleModeChange}
          >
            {Object.entries(FILTER_MODE_LABELS).map(([mode, label]) => (
              <MenuItem
                data-testid={ColumnFilterMenuTestIds.getModeOptionId(mode)}
                key={mode}
                value={mode}
              >
                {label}
              </MenuItem>
            ))}
          </Select>
          <TextFieldCDS
            autoFocus
            dataTestId={ColumnFilterMenuTestIds.VALUE_INPUT}
            name="column-filter-value"
            placeholder="Filter..."
            size="small"
            sx={filterInputStyles}
            value={localValue}
            onChange={handleValueChange}
            onKeyDown={handleKeyDown}
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              data-testid={ColumnFilterMenuTestIds.CLEAR_BUTTON}
              variant="line"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button data-testid={ColumnFilterMenuTestIds.APPLY_BUTTON} onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

ColumnFilterMenu.propTypes = {
  columnKey: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  externalAnchorEl: PropTypes.object,
  filterMode: PropTypes.string,
  filterValue: PropTypes.string,
  onClose: PropTypes.func,
};

export default memo(ColumnFilterMenu);
